
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    location: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3m6kdkt07omtp5lr01g', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          service: '',
          location: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-black">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get Your Free <span className="text-yellow-500">Quote</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Ready to secure your property? Contact our experts for a free consultation and customized bollard solution.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-yellow-500/20">
            <h3 className="text-2xl font-bold text-white mb-6">Request Quote</h3>
            
            {submitStatus === 'success' && (
              <div className="bg-green-500/20 border border-green-500 rounded-lg p-4 mb-6">
                <p className="text-green-400">Thank you! Your quote request has been submitted. We'll contact you within 24 hours.</p>
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 mb-6">
                <p className="text-red-400">Sorry, there was an error submitting your request. Please try again or call us directly.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} data-readdy-form id="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-white font-semibold mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-white font-semibold mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                    placeholder="01245 123456"
                  />
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Company/Organization</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                    placeholder="Your company name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-white font-semibold mb-2">Service Required *</label>
                  <div className="relative">
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 pr-8 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none appearance-none cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="telescopic-bollards">Telescopic Bollards</option>
                      <option value="fold-down-bollards">Fold Down Bollards</option>
                      <option value="electric-bollards">Electric Bollards</option>
                      <option value="maintenance-repair">Maintenance & Repair</option>
                      <option value="consultation">Security Consultation</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
                <div>
                  <label className="block text-white font-semibold mb-2">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none"
                    placeholder="Chelmsford, Essex"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-white font-semibold mb-2">Project Details</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded-lg text-white text-sm focus:border-yellow-500 focus:outline-none resize-none"
                  placeholder="Please describe your security requirements, site details, and any specific needs..."
                ></textarea>
                <p className="text-gray-400 text-xs mt-1">{formData.message.length}/500 characters</p>
              </div>

              <Button 
                type="submit" 
                size="lg" 
                className="w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Submitting...
                  </>
                ) : (
                  <>
                    Get Free Quote
                    <i className="ri-send-plane-line ml-2"></i>
                  </>
                )}
              </Button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-yellow-500/20">
              <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-map-pin-line text-black text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Head Office</h4>
                    <p className="text-gray-300">123 High Street<br />Chelmsford, Essex CM1 1AB</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-phone-line text-black text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Phone</h4>
                    <a href="tel:+447834858178" className="text-gray-300 hover:text-yellow-500 cursor-pointer">+44 7834 858178</a>
                    <p className="text-gray-400 text-sm">24/7 Emergency: <a href="tel:+447834858178" className="hover:text-yellow-500 cursor-pointer">+44 7834 858178</a></p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-mail-line text-black text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Email</h4>
                    <p className="text-gray-300">info@bollardinstallationessex.co.uk</p>
                    <p className="text-gray-400 text-sm">quotes@bollardinstallationessex.co.uk</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-500 rounded-lg flex items-center justify-center mr-4">
                    <i className="ri-time-line text-black text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Business Hours</h4>
                    <p className="text-gray-300">Monday - Friday: 8:00 AM - 6:00 PM</p>
                    <p className="text-gray-300">Saturday: 9:00 AM - 4:00 PM</p>
                    <p className="text-gray-400 text-sm">Emergency service available 24/7</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 border border-yellow-500/20">
              <h3 className="text-xl font-bold text-white mb-4">Service Areas</h3>
              <div className="grid grid-cols-2 gap-3">
                {['Chelmsford', 'Colchester', 'Basildon', 'Southend-on-Sea', 'Harlow', 'Braintree', 'Brentwood', 'Maldon'].map((area, index) => (
                  <div key={index} className="flex items-center">
                    <i className="ri-map-pin-2-line text-yellow-500 mr-2"></i>
                    <span className="text-gray-300">{area}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-400 text-sm mt-4">
                We also serve surrounding areas. Contact us to confirm coverage for your location.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
